import React, {useEffect, useState, useRef} from "react";
import { MatrixClient } from "matrix-js-sdk";
import { useGameDetailConfig } from "../../context/GameDetailConfig"; // For access token
import { logActivity } from "../../utils/activity";
import { useHints } from "../../hints/HintContext";
import HintPanel, { HintHeaderSummary } from "../HintPanel/HintPanel";
import "./ChatInterface.css";

const ChatInterface = () => {
    const [matrixClient, setMatrixClient] = useState(null); // Matrix Client instance
    const {gameDetailConfig} = useGameDetailConfig();
    // Hint panel coupling (SCRUM-686): a picked hint lands in the input box, and a sent message
    // that started as a hint is reported back with its riddle + level. Optional: null without
    // a HintProvider. The ref keeps the Matrix listeners' closure from going stale.
    const hints = useHints();
    const hintsRef = useRef(hints);
    hintsRef.current = hints;
    const draftRef = useRef(null);
    const [rooms, setRooms] = useState([]); // List of rooms the user has access to
    const [selectedRoom, setSelectedRoom] = useState(null); // Currently active room
    const [messages, setMessages] = useState([]); // Messages in the active room
    const [newMessage, setNewMessage] = useState(""); // Draft of user message
    const [isProcessing, setIsProcessing] = useState(false);
    const inputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false); // Expand/minimize state

    // Set focus to the input field after the component renders
    useEffect(() => {
        if (!isProcessing && inputRef.current) {
            inputRef.current.focus(); // Focus input only if it's mounted
        }
    }, [isProcessing]);   // Function to scroll to the bottom of the chat
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" }); // Smooth scrolling
        }
    };

    // Scroll to the bottom whenever messages are updated
    useEffect(() => {
        scrollToBottom(); // Scroll when `messages` changes
    }, [messages]); // Dependency on `messages`

    // A hint picked in the panel becomes the draft. The GM can still edit it before sending.
    useEffect(() => {
        const draft = hints?.draft;
        if (!draft) return;
        draftRef.current = draft;
        setNewMessage(draft.text);
        setIsExpanded(true);
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.style.height = "auto";
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    }, [hints?.draft]);

    // The chat may have been folded when the hint arrived: the box only exists once expanded.
    useEffect(() => {
        if (isExpanded && draftRef.current && inputRef.current) inputRef.current.focus();
    }, [isExpanded]);


    useEffect(() => {
        if (!gameDetailConfig?.chat_token) return;

        // Initialize Matrix client
        const client = new MatrixClient({
            baseUrl: gameDetailConfig.chat_path,
            accessToken: gameDetailConfig.chat_token,
        });
        client
            .whoami()
            .then((response) => {
                console.log("Fetched user ID: ", response.user_id);
                client.credentials.userId = response.user_id;
                // Start the client
                client.startClient();
                setMatrixClient(client);
            })
            .catch((error) => {
                console.error("Failed to fetch user ID:", error);
            });

        // Cleanup on unmount or token changes
        return () => {
            if (client) {
                client.stopClient();
            }
        };
    }, [gameDetailConfig]);

    useEffect(() => {
        if (!matrixClient) return;

        // Handle sync and fetch joined rooms
        // Listen for new events in the selected room
        const onRoomTimeline = (event, room) => {
            if (event.getType() === "m.room.message") {
                if (!event.isRedacted()) {
                    const message = {
                        id: event.getId(),
                        sender: event.getSender(),
                        content: event.getContent().body,
                        timestamp: event.getTs(),
                        roomName: room.name,
                        roomId: room.roomId,
                    };

                    // Append the new message to the chat
                    setMessages((prev) => [...prev, message]);

                    // Players' own messages are a "where are they" signal for the hint panel.
                    if (event.getSender() !== matrixClient.credentials?.userId) {
                        hintsRef.current?.notePlayerMessage(message.content, message.timestamp);
                    }
                }
            } else if (event.getType() === "m.room.redaction") {
                const redactedId = event.event.redacts; // Get the ID of the redacted message

                if (redactedId) {
                    // Remove the redacted message from the chat state
                    setMessages((prev) => prev.filter((msg) => msg.id !== redactedId));
                    console.log(`Message with ID ${redactedId} was redacted.`);
                }
            }
        };

        matrixClient.on("Room.timeline", onRoomTimeline); // Using SDK listener for new events

        const onSync = (state, prevState, data) => {
            if (state === "PREPARED") {
                console.log("Matrix client is ready, sync completed.");
            } else {
                if (state === "ERROR") {
                    console.error("Sync error:", data?.error);
                } else {
                    console.log(`Sync state: ${state}`);
                }
            }
        };
        matrixClient.on("sync", onSync);

        const onRoom = (room) => {
            console.log("New room loaded:", room);
            setRooms((prevRooms) => {
                const newRoom = {
                    id: room.roomId,
                    name: room.name || room.roomId,
                };

                // if no room is selected, select this one
                if (!selectedRoom) {
                    setSelectedRoom(newRoom);
                }
                // Avoid duplicate rooms in state
                const existingRoom = prevRooms.find((r) => r.id === room.roomId);
                if (!existingRoom) {
                    return [...prevRooms, newRoom];
                }
                return prevRooms;
            });
        };
        matrixClient.on("Room", onRoom);

        // Cleanup the event listener on unmount
        return () => {
            matrixClient.removeListener("Room.timeline", onRoomTimeline);
            matrixClient.removeListener("Room", onRoom);
            matrixClient.removeListener("sync", onSync);
        };
    }, [matrixClient, selectedRoom]);

    // Handle sending a message
    const sendMessage = async () => {
        if (!newMessage.trim()) return;

        const messageText = newMessage.trim();

        // Local development without a Matrix room: pretend the message went out so the hint flow
        // (sent-marking, next level) can be exercised. The message is shown with a "not sent"
        // marker and nothing leaves the browser. Never in a production build.
        if (!selectedRoom || !matrixClient) {
            if (process.env.NODE_ENV !== "development") return;
            setMessages((prev) => [...prev, {
                id: `local-${Date.now()}`,
                sender: "local",
                content: `${messageText}  (ikke sendt — ingen chat lokalt)`,
                timestamp: Date.now(),
            }]);
            const draft = draftRef.current;
            if (draft) {
                hints?.markSent({ riddleId: draft.riddleId, level: draft.level, text: messageText, edited: messageText !== draft.text.trim() });
                draftRef.current = null;
            }
            setNewMessage("");
            if (inputRef.current) inputRef.current.style.height = "auto";
            return;
        }

        try {
            await retryWithBackoff(matrixClient.sendEvent.bind(matrixClient),
                [selectedRoom.id,
                    "m.room.message",
                    {
                        msgtype: "m.text",
                        body: messageText,
                    },
                    ""], // txnId is optional, SDK handles it automatically
            );
            // When the message began as a hint from the panel, log it structured: riddle, level,
            // minutes into the game and whether the GM rewrote it. That row is what a later
            // "smart" layer learns from. Free-text messages log as before.
            const draft = draftRef.current;
            if (draft) {
                const edited = messageText !== draft.text.trim();
                const min = hints?.elapsedSec != null ? Math.floor(hints.elapsedSec / 60) : "?";
                logActivity(
                    "hint_sent",
                    `${selectedRoom.name}: ${messageText} [gåde=${draft.riddleId} niveau=${draft.level} sprog=${draft.lang || "da"} min=${min} redigeret=${edited ? "ja" : "nej"}]`
                );
                hints?.markSent({ riddleId: draft.riddleId, level: draft.level, text: messageText, edited });
                draftRef.current = null;
            } else {
                logActivity("hint_sent", `${selectedRoom.name}: ${messageText}`);
            }
            setNewMessage(""); // Clear the input field
        } catch (error) {
            console.error("Failed to send message:", error);
        } finally {
            setIsProcessing(false);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }

    };

    // Handle clearing chat for all rooms
    const clearChatForAllRooms = () => {
        if (!messages.length) return;

        const confirmation = window.confirm("This will clear all messages. Are you sure?");
        if (!confirmation) return;

        setIsProcessing(true);
        (async () => {
            try {
                for (let i = messages.length - 1; i >= 0; i--) {
                    const message = messages[i];
                    try {
                        await retryWithBackoff(
                            matrixClient.redactEvent.bind(matrixClient),
                            [message.roomId, message.id]
                        );
                        console.log(`Message ${message.id} redacted successfully.`);
                    } catch (error) {
                        // Handle individual redaction errors
                        console.error(`Failed to redact message ${message.id}:`, error);
                    }
                }
                console.log("All messages redacted.");

            } catch (error) {
                console.error("Failed to redact messages:", error);
            } finally {
                setIsProcessing(false);
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }
        })();
        console.log("Redaction started in the background.");
    };
    /**
     * Generic retry logic for handling Matrix API calls with rate-limiting.
     *
     * @param {Function} apiCall - The API function to call (should return a Promise).
     * @param {Array} args - Arguments to pass to the API function.
     * @param {number} [retryAttempts=5] - Maximum number of retry attempts.
     * @param {number} [defaultRetryAfterMs=1000] - Default retry delay if `retry_after_ms` is not provided by the server.
     * @returns {Promise} - Resolves or rejects based on the API call result.
     */
    const retryWithBackoff = async (apiCall, args, retryAttempts = 5, defaultRetryAfterMs = 1000) => {
        try {
            // Attempt the API call
            return await apiCall(...args);
        } catch (error) {
            if (error?.errcode === "M_LIMIT_EXCEEDED" && retryAttempts > 0) {
                setIsProcessing(true);
                // Get the `retry_after_ms` from the response, fallback to a default value
                const retryAfterMs = error.data?.retry_after_ms || defaultRetryAfterMs;

                console.warn(
                    `Rate limit hit for ${apiCall.name}. Retrying in ${retryAfterMs}ms. Attempts left: ${retryAttempts - 1}`
                );

                // Wait for `retry_after_ms` before retrying
                await new Promise((resolve) => setTimeout(resolve, retryAfterMs));

                // Retry the API call with remaining attempts
                return retryWithBackoff(apiCall, args, retryAttempts - 1, defaultRetryAfterMs);
            }

            // If retry limit is reached or another error occurs, rethrow the error
            console.error(`Error during API call ${apiCall.name}:`, error);
            throw error;
        }
    };

    const formattedTime = (timestamp) => {
        if (!timestamp) {
            return "";
        }
        const date = new Date(timestamp);
        const hours = date.getHours();
        let minutes = date.getMinutes();
        if(minutes < 10 ) {
            minutes = "0" + minutes;
        }
        return `${hours}:${minutes}`;
    };

    // Toggle chat bubble state
    const toggleExpand = () => {
        setIsExpanded((prevState) => !prevState);
    };

    return (
        <div className="chat-bubble-wrapper">
            {/* Chat Bubble (Minimized View) */}
            {!isExpanded && (
                <div className="chat-bubble-minimized" onClick={toggleExpand}>
                    <p>Chat<HintHeaderSummary /></p>
                </div>
            )}

            {/* Expanded Chat Interface */}
            {isExpanded && (
                <div className="chat-bubble">
                    {/* The whole header minimizes, like the minimized bubble expands on any click. */}
                    <div className="chat-header" onClick={toggleExpand} role="button" title="Minimize chat">
                        <h3>Chat<HintHeaderSummary /></h3>
                        <button
                            className="chat-minimize-btn"
                            tabIndex={-1}
                            aria-hidden="true"
                        >−</button>
                    </div>

                    {/* Room Selector */}
                    <div className="room-selector">
                        {rooms.length > 1 && (
                            <>
                                <label htmlFor="room-dropdown">Select Room:</label>
                                <select
                                    id="room-dropdown"
                                    value={selectedRoom || ""}
                                    onChange={(e) => setSelectedRoom(e.target.value)}
                                >
                                    {rooms.map((room) => (
                                        <option key={room.id} value={room.id}>
                                            {room.name}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                        <button
                            className="chat-action-btn"
                            onClick={clearChatForAllRooms}
                            disabled={isProcessing}
                        >
                            Ryd
                        </button>
                    </div>

                    {/* Chat Messages */}
                    <div className="chat-messages">
                        {messages.length === 0 ? (
                            <p>No messages available.</p>
                        ) : (
                            messages.map((message, index) => (
                                <div key={index} className="chat-message">
                                    <div className="chat-message-timestamp">
                                        {formattedTime(message.timestamp)}
                                    </div>
                                    <div className="chat-message-message">
                                        {message.content}
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Hints (SCRUM-686): guess at the current riddle + the next hint to use. */}
                    <HintPanel />

                    {/* Message Input */}
                    <div className="message-input">
                        <div className="textarea-wrapper">
                            <textarea
                                ref={inputRef}
                                placeholder="Type a message..."
                                value={newMessage}
                                onChange={(e) => {
                                    setNewMessage(e.target.value);
                                    // Emptying the box, or replacing its whole content in one go
                                    // (select-all and type), drops the hint draft: what follows is
                                    // free text, not an edited hint.
                                    const v = e.target.value;
                                    if (v === "" || (draftRef.current && newMessage.length > 10 && v.length <= 2)) draftRef.current = null;
                                    e.target.style.height = "auto";
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage();
                                    }
                                }}
                            />
                        </div>
                        <button
                            className="chat-send-btn"
                            onClick={sendMessage}
                            disabled={isProcessing}
                        >
                            Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatInterface;